'use client';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Layout from '../lib/components/Layout';
import type { CompanyEventDetails, CompanyEventsQueryParams, ISetParams } from '@benzinga/calendar-manager';
import { SessionContext } from '@benzinga/session-context';

import { ICalendarProps, IDateRange } from '../lib/interfaces';

import { initialCompanyEventsFetchParams } from './calendarParams';
import { COMPANY_EVENTS_PAGE_SIZE, defaultCompanyEventsDateRange } from './constants';
import ColumnsDefinition from './ColumnsDef';
import { fetchCompanyEvents } from './Services';

export { default as EventsCalendarColumnsDef } from './ColumnsDef';
export { fetchCompanyEvents } from './Services';

export const EventsCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const session = useContext(SessionContext);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<CompanyEventDetails[]>(initialData ?? []);

  const dateRange: IDateRange | null = useMemo(
    () => (initialDateRange === undefined ? defaultCompanyEventsDateRange() : initialDateRange),
    [initialDateRange],
  );

  useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData ?? []);
    }
  }, [initialData, calendarData?.serverSide]);

  const fetchData = useCallback(
    (filterParams: CompanyEventsQueryParams | ISetParams) => {
      setLoading(true);
      fetchCompanyEvents(session.getSession(), {
        ...filterParams,
        pageSize: COMPANY_EVENTS_PAGE_SIZE,
      })
        .then(setTableData)
        .finally(() => setLoading(false));
    },
    [session],
  );

  const onTableReady = useCallback(() => {
    if (initialData != null) {
      setTableData(initialData);
    } else {
      fetchData(initialCompanyEventsFetchParams(dateRange, initialTicker));
    }
    if (onReady) {
      setTimeout(onReady);
    }
  }, [dateRange, fetchData, initialData, initialTicker, onReady]);

  return (
    <Layout
      additionalGridOptions={{
        getRowHeight: params => (params.node?.data?.isProBanner ? 140 : undefined),
      }}
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      leftSlot={leftSlot}
      onFilterChanged={fetchData}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      theme="modern"
      ticker={initialTicker}
    />
  );
};
